import Mousetrap from 'mousetrap';
import { findRelDragStartPoint, getPointFromAngles } from 'lib/geom';
import { rui } from 'lib/relationship-ui';
import { zoomUI } from 'lib/zoom-ui';
import { nui } from 'lib/nodes-ui';
import { copyToClipboard } from 'lib/util.js';
import { pui } from 'lib/perspectives-ui';
import { regionUI } from 'lib/region-ui';
import store from '../store_instance';
import presentationUI from 'lib/presentation-ui';
import EventTracker from 'lib/event-tracking';

const $ = window.jQuery;

export const setupShortcuts = canvas => {
  const $canvas = $(canvas.refs.canvas);
  const { isMac } = canvas;

  Mousetrap.bind(['command+shift+f','ctrl+shift+f'], (e) => {
    //not sure about this, might mess with accessibility
    canvas.toggleZenMode();
  });

  Mousetrap.bind(['n'], (e) => {
    e.stopPropagation();
    e.preventDefault();
    const { scale, tx, ty } = canvas.getMatrix();
    canvas.lastNodeCreatedWithEnterKey = null;

    let x = canvas.mouseX;
    let y = canvas.mouseY;
    if (x === 0 && y === 0) {
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;
    }

    const scaledX = (x - tx - (64 * scale)) / scale;
    const scaledY = (y - ty - (24 * scale)) / scale;

    canvas.createNewNode({ x: scaledX, y: scaledY, fromShortcut: true });
    canvas.appFooterTag.updateStyle();
  });

  Mousetrap.bind(['left'], (e) => {
    if (presentationUI.isPresenting) {
      presentationUI.prev();
      e.preventDefault();

      EventTracker.track({
        category: 'Shortcut',
        action: 'Presentation-Left',
        label: 'Presentation',
      });
    } else if (regionUI.getSelectedRegionIdsArray().length) {
      regionUI.getSelectedRegionIdsArray().forEach(regionUUID =>
        regionUI.nudgeRegion({
          regionUUID,
          dx: -10,
        })
      );

      EventTracker.track({
        category: 'Shortcut',
        action: 'Region-Nudge-Left',
        label: 'Region',
      });
    } else if (canvas.tags.gallery.isVisible) {
      canvas.tags.gallery.prev();
    } else {
      $canvas.panzoom('pan', 10, 0, { relative: true });

      EventTracker.debouncedTrack({
        category: 'Shortcut',
        action: 'Pan-Left',
        label: 'Pan',
      });
    }
  });

  Mousetrap.bind(['up'], (e) => {
    if (presentationUI.isPresenting) {
      presentationUI.prev();
      e.preventDefault();

      EventTracker.track({
        category: 'Shortcut',
        action: 'Presentation-Up',
        label: 'Presentation',
      });
    } else if (regionUI.getSelectedRegionIdsArray().length) {
      regionUI.getSelectedRegionIdsArray().forEach(regionUUID =>
        regionUI.nudgeRegion({
          regionUUID,
          dy: -10,
        })
      );

      EventTracker.track({
        category: 'Shortcut',
        action: 'Region-Nudge-Up',
        label: 'Region',
      });
    } else {
      $canvas.panzoom('pan', 0, 10, { relative: true });

      EventTracker.debouncedTrack({
        category: 'Shortcut',
        action: 'Pan-Up',
        label: 'Pan',
      });
    }
  });

  Mousetrap.bind(['down'], (e) => {
    if (presentationUI.isPresenting) {
      presentationUI.next();
      e.preventDefault();

      EventTracker.track({
        category: 'Shortcut',
        action: 'Presentation-Down',
        label: 'Presentation',
      });
    } else if (regionUI.getSelectedRegionIdsArray().length) {
      regionUI.getSelectedRegionIdsArray().forEach(regionUUID =>
        regionUI.nudgeRegion({
          regionUUID,
          dy: 10,
        })
      );

      EventTracker.track({
        category: 'Shortcut',
        action: 'Region-Nudge-Down',
        label: 'Region',
      });
    } else {
      $canvas.panzoom('pan', 0, -10, { relative: true });

      EventTracker.debouncedTrack({
        category: 'Shortcut',
        action: 'Pan-Down',
        label: 'Pan',
      });
    }
  });

  Mousetrap.bind(['right'], (e) => {
    if (presentationUI.isPresenting) {
      presentationUI.next();
      e.preventDefault();

      EventTracker.track({
        category: 'Shortcut',
        action: 'Presentation-Right',
        label: 'Presentation',
      });
    } else if (regionUI.getSelectedRegionIdsArray().length) {
      regionUI.getSelectedRegionIdsArray().forEach(regionUUID =>
        regionUI.nudgeRegion({
          regionUUID,
          dx: 10,
        })
      );

      EventTracker.track({
        category: 'Shortcut',
        action: 'Region-Nudge-Right',
        label: 'Region',
      });
    } else if (canvas.tags.gallery.isVisible) {
      canvas.tags.gallery.next();
    } else {
      $canvas.panzoom('pan', -10, 0, { relative: true });

      EventTracker.debouncedTrack({
        category: 'Shortcut',
        action: 'Pan-Right',
        label: 'Pan',
      });
    }
  });

  Mousetrap.bind(['ctrl+left'], () => {
    $canvas.panzoom('pan', 100, 0, { relative: true });

    EventTracker.debouncedTrack({
      category: 'Shortcut',
      action: 'Pan-Left',
      label: 'Pan',
    });
  });

  Mousetrap.bind(['ctrl+up'], () => {
    $canvas.panzoom('pan', 0, 100, { relative: true });

    EventTracker.debouncedTrack({
      category: 'Shortcut',
      action: 'Pan-Up',
      label: 'Pan',
    });
  });

  Mousetrap.bind(['ctrl+down'], () => {
    $canvas.panzoom('pan', 0, -100, { relative: true });

    EventTracker.debouncedTrack({
      category: 'Shortcut',
      action: 'Pan-Down',
      label: 'Pan',
    });
  });

  Mousetrap.bind(['ctrl+right'], () => {
    $canvas.panzoom('pan', -100, 0, { relative: true });

    EventTracker.debouncedTrack({
      category: 'Shortcut',
      action: 'Pan-Right',
      label: 'Pan',
    });
  });

  // alt + command + plus is handled in app.html because mousetrap doesn't like it
  Mousetrap.bind(['+', '='], e => {
    if (canvas.tags.gallery.isVisible) return;

    e.preventDefault();
    $canvas.panzoom('zoom', zoomUI.roundScale(zoomUI.currentScale + zoomUI.increment));

    EventTracker.track({
      category: 'Shortcut',
      action: 'Zoom-In',
      label: 'Zoom',
    });
  });

  Mousetrap.bind(['alt+meta+-', '-'], e => {
    if (canvas.tags.gallery.isVisible) return;

    e.preventDefault();
    $canvas.panzoom('zoom', zoomUI.roundScale(zoomUI.currentScale - zoomUI.increment));

    EventTracker.track({
      category: 'Shortcut',
      action: 'Zoom-Out',
      label: 'Zoom',
    });
  });

  Mousetrap.bind(['ctrl+f', 'command+f'], e => {
    const { searchModal } = canvas;

    e.preventDefault();

    searchModal.show();

    if (!searchModal.open) {
      EventTracker.track({
        category: 'Shortcut',
        action: 'Open-Search',
        label: 'Search',
      });
    }
  });

  Mousetrap.bind(['ctrl+g', 'command+g'], () => {
    if (canvas.isViewOnly()) return false;
    const selectedNodes = canvas.getRootSelectedNodes();
    if (selectedNodes.length < 1) return false;

    canvas.createSystemFromSelection();
    canvas.selectionArea.resetSelectionBox();

    EventTracker.track({
      category: 'Shortcut',
      action: 'Group-Selection',
      label: 'Selection',
    });

    canvas.bulkActionsMenuTag.update();
    return false;
  });

/*
todo: update this to do bulk free-part
  Mousetrap.bind(['ctrl+shift+g', 'command+shift+g'], () => {
    if (canvas.isViewOnly() || !canvas.selectedElements.length) { return false; }

    const selected = canvas.getSelectedElements();
    const ids = canvas.store.sortByDepth(selected.nodes.map(n => n.id));
    const operationMessages = []
    for (const id of ids.reverse()) {
      const message = nui.ungroupSystem(id);
      operationMessages.push(message);
    }
    canvas.selectionArea.resetSelectionBox();
    canvas.bulkActionsMenuTag.update();
    canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: err => {
        console.warn(`ungroup system batchOperation failed: ${err}`);
      }
    });
    return false;
  });
*/
  // select all shortcut
  Mousetrap.bind(['ctrl+a', 'command+a'], (e) => {
    e.preventDefault();
    const elementTags = [];
    for (const node of store.getNodes()) {
      if (canvas.nodeTags[node.id]) {
        elementTags.push(canvas.nodeTags[node.id]);
      }
    }

    for (const link of store.getLinks()) {
      if (canvas.linkTags[link.id]) {
        elementTags.push(canvas.linkTags[link.id]);
      }
    }

    canvas.setSelectedElements(elementTags);

    regionUI.selectRegions(store.getRegions().map(r => r.id));

    EventTracker.track({
      category: 'Shortcut',
      action: 'Select All',
      label: 'Selection',
    });
  });

  Mousetrap.bind(['escape'], () => {

    if (canvas.importModal.isOpen()) {
      canvas.importModal.dismiss();
    }
    if (canvas.searchModal.open) {
      canvas.searchModal.dismiss();

      EventTracker.track({
        category: 'Shortcut',
        action: 'Dismiss-Search',
        label: 'Search',
      });
    }

    if (presentationUI.isPresenting) {
      presentationUI.stop();

      EventTracker.track({
        category: 'Shortcut',
        action: 'Presentation-Stop',
        label: 'Presentation',
      });
    }

    if (pui.hasPersistentPerspective()) {
      pui.unsetAllPerspectives();
    }

    if (canvas.loadedTemplate) {
      canvas.creationTools.clearSelection();
    }

    if (canvas.tags.gallery.isVisible) {
      canvas.tags.gallery.closeGallery();
    }
  });

  Mousetrap.bind(['del', 'backspace'], () => {
    canvas.handleDelete();

    EventTracker.track({
      category: 'Shortcut',
      action: 'Delete',
      label: 'Selection',
    });
  });

  Mousetrap.bind(['ctrl+z', 'command+z'], () => {
    if (canvas.isViewOnly()) { return; }

    EventTracker.track({
      category: 'Shortcut',
      action: 'Undo',
    });

    (async () => {
      await canvas.operation.undo();
      canvas.closeUndoBanner();
      if (canvas.collapsedCardAdoptionPopoverTag.isOpen) {
        canvas.collapsedCardAdoptionPopoverTag.hide();
      }
    })();
    canvas.bulkActionsMenuTag.update();
    return false;
  });

  Mousetrap.bind(['ctrl+y', 'command+y', 'ctrl+shift+z', 'command+shift+z'], () => {
    if (canvas.isViewOnly()) { return; }
    EventTracker.track({
      category: 'Shortcut',
      action: 'Redo',
    });

    (async () => {
      await canvas.operation.redo();
      canvas.bulkActionsMenuTag.update();
    })();

    return false;
  });

  Mousetrap.bind(['ctrl+enter', 'command+enter'], () => {
    if (canvas.isViewOnly()) { return; }
    const selectedLinks = canvas.getSelectedLinks();
    const selectedNodes = canvas.getSelectedNodes();
    if (selectedLinks.length == 1) {
      const link = selectedLinks[0];
      if (link.linkNode()) return false;
      const pointA = { x: link.fromNode().x, y: link.fromNode().y };
      const angleA = link.angleStartToR;
      const pointB = { x: link.toNode().x, y: link.toNode().y };
      const angleB = link.angleEndToR;
      canvas.hidePopups();
      canvas.createNewNode(getPointFromAngles(pointA, angleA, pointB, angleB), link);
    } else if (selectedNodes.length == 1) {
      const selectedNode = canvas.nodeTags[selectedNodes[0].id];
      canvas.hidePopups();
      selectedNode.addPart();
    }
    return false;
  });

  Mousetrap.bind(['l'], rui.startCreationFromClick.bind(rui));

  Mousetrap.bind([(isMac ? 'shift+command+enter' : 'ctrl+shift+f5')], e => {
    e.preventDefault();
    if (canvas.waypointsTabTag.waypoints.length > 0) {
      canvas.waypointsTabTag.startPresentation();
    } else {
      canvas.toolsSidebarTag.activateTab({
        tabTagName: 'waypoints-tab',
        push: true
      });
    }
  });

  Mousetrap.bind([(isMac ? 'command+enter' : 'ctrl+f5')], e => {
    e.preventDefault();
    if (canvas.waypointsTabTag.waypoints.length > 0) {
      canvas.waypointsTabTag.resumePresentation();
    } else {
      canvas.toolsSidebarTag.activateTab({
        tabTagName: 'waypoints-tab',
        push: true
      });
    }
  });

  Mousetrap.bind(['|'], () => {
    if (canvas.isViewOnly()) { return; }
    const selectedLinks = canvas.getSelectedLinks();
    if (selectedLinks.length == 1) {
      const link = selectedLinks[0];
      canvas.hidePopups();
      canvas.linkTags[link.id].straightenLine();
    }
    return false;
  });

  Mousetrap.bind(['ctrl+0', 'command+0'], () => {
    canvas.fitSelectionOrMap();

    EventTracker.track({
      category: 'Shortcut',
      action: 'Fit to Screen',
      label: 'Selection',
    });
  });

  Mousetrap.bind(['ctrl+shift+l', 'command+shift+l'], () => {
    const selectedNodes = canvas.getSelectedNodes();
    const selectedRegionIds = regionUI.getSelectedRegionIdsArray();

    if (selectedNodes.length > 0) {
      const commaSeparatedIds = selectedNodes.map(n => n.id).join(',');
      const url = `${canvas.config.site_url}/maps/${canvas.access.publicIdentifier}/edit/${canvas.access.editKey}?nodeid=${commaSeparatedIds}`;

      window.history.pushState({}, '', url);
      copyToClipboard({text: url});

      canvas.centerContent({ nodes: selectedNodes });
      canvas.notificationTag.show({ message: 'Link copied to clipboard' });

      EventTracker.track({
        category: 'Shortcut',
        action: 'Link to Selection',
        label: 'Selection',
      });
    } else if (selectedRegionIds.length) {
      const url = `${canvas.config.site_url}/maps/${canvas.access.publicIdentifier}/edit/${canvas.access.editKey}?regionId=${selectedRegionIds.join(',')}`;

      window.history.pushState({}, '', url);
      copyToClipboard({text: url});

      canvas.centerContent({ regions: selectedRegionIds.map(id => ({ id }))});
      canvas.notificationTag.show({ message: 'Link copied to clipboard' });

      EventTracker.track({
        category: 'Shortcut',
        action: 'Link to Region',
        label: 'Region',
      });
    }

    return false;
  });

  Mousetrap.bind(['?'], () => {
    if (canvas.sidebarStatus === 'shortcuts') {
      canvas.closeSidebar();
    } else {
      canvas.openSidebar('shortcuts');
    }
  });
};

export default { setupShortcuts };



// WEBPACK FOOTER //
// ./starling/src/lib/shortcuts.js